// src/hooks/useGeoPermissionPrimer.js
import { useState, useMemo, useEffect, useCallback } from "react";
import {
  hasCachedGeoGrant,
  rememberGeoGrant,
} from "../utils/geoPermissionStorage";

const GEO_PRIMER_SHOWN_ONCE = "inssa:geo:primerShownOnce";

const isIOSSafari = (() => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iP(hone|ad|od)/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
})();

export default function useGeoPermissionPrimer({
  permissionState,        // 'prompt' | 'granted' | 'denied' | 'unsupported' | 'unknown'
  userLocation,           // {lat, lng} | null
  requestInitialLocation, // async fn -> triggers browser prompt
}) {
  // iOS "granted" stickiness workaround (helps when iOS reports "prompt")
  const [iosGeoGrantedCache, setIosGeoGrantedCache] = useState(() => {
    if (!isIOSSafari) return false;
    return hasCachedGeoGrant();
  });

  useEffect(() => {
    if (!isIOSSafari) return;
    if (userLocation && typeof localStorage !== "undefined") {
      rememberGeoGrant();
      setIosGeoGrantedCache(true);
    }
  }, [userLocation]);

  // Determine effective permission (with iOS smoothing)
  const effectivePermissionState = useMemo(() => {
    if (!isIOSSafari) return permissionState;
    if (permissionState === "denied") return "denied";
    return (iosGeoGrantedCache || !!userLocation) ? "granted" : "prompt";
  }, [permissionState, iosGeoGrantedCache, userLocation]);

  // One-time primer flag
  const [primerShownOnce, setPrimerShownOnce] = useState(() => {
    try { return localStorage.getItem(GEO_PRIMER_SHOWN_ONCE) === "1"; } catch { return false; }
  });

  // Show primer only if:
  // 1) we haven't shown it before, AND
  // 2) permission isn't granted yet AND we don't have a location.
  const permissionDialogOpen =
    !primerShownOnce &&
    (effectivePermissionState === "prompt" ||
     effectivePermissionState === "unknown" ||
     effectivePermissionState === "unsupported") &&
    !userLocation;

  // If the user ever grants or denies, mark primer as "shown once" so it never reappears.
  useEffect(() => {
    if (effectivePermissionState === "granted" || effectivePermissionState === "denied") {
      if (!primerShownOnce) {
        try { localStorage.setItem(GEO_PRIMER_SHOWN_ONCE, "1"); } catch {}
        setPrimerShownOnce(true);
      }
    }
  }, [effectivePermissionState, primerShownOnce]);

  // Hard-block overlay when explicitly denied and we don't have a fix
  const showDeniedOverlay = effectivePermissionState === "denied" && !userLocation;

  // Suppress ad only while the FIRST primer is visible or when denied overlay is shown
  const suppressAd = permissionDialogOpen || showDeniedOverlay;

  // AFTER the one-time primer has been shown (or dismissed),
  // ensureLocationOrPrompt goes directly to the browser prompt when needed.
  const ensureLocationOrPrompt = useCallback(() => {
    // If already granted AND we have a location, proceed.
    if (effectivePermissionState === "granted" && userLocation) return true;

    // If denied with no location, show hard overlay, do not attempt prompt.
    if (showDeniedOverlay) return false;

    // Otherwise, go straight to browser prompt (no primer).
    requestInitialLocation(); // let caller return now; once granted it will proceed on next click
    return false;
  }, [effectivePermissionState, userLocation, showDeniedOverlay, requestInitialLocation]);

  // NEW: allow the primer dialog to be explicitly dismissed via "Not now"
  const closePrimer = useCallback(() => {
    if (!primerShownOnce) {
      try { localStorage.setItem(GEO_PRIMER_SHOWN_ONCE, "1"); } catch {}
      setPrimerShownOnce(true);
    }
  }, [primerShownOnce]);

  return {
    permissionDialogOpen,
    showDeniedOverlay,
    suppressAd,
    ensureLocationOrPrompt,
    closePrimer, // expose explicit closer for "Not now"
  };
}
